// Explore-stats page: Party representation by state — current Parliament.
// Recreates the Handbook page — stacked bar state × party and party ×
// state, with a members/senators filter and a state × party matrix table.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatStackedBar } from './charts';

export default function PartyRepresentationByState() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chamber, setChamber] = useState('all');

  useEffect(() => {
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const res = await getExploreStat('party-representation-by-state',
          chamber === 'all' ? {} : { chamber });
        if (active) { setData(res); setError(null); }
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [chamber]);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Party representation by state — current Parliament"
      description="The number of seats each party holds in each state or territory in the current Parliament, and the proportion of each party's representation across the states."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="party-representation-by-state"
    >
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={chamber}
                           onChange={(e, v) => v && setChamber(v)}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="members">Members</ToggleButton>
          <ToggleButton value="senators">Senators</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              States by party representation
            </Typography>
            <StatStackedBar data={charts.by_state || []} xKey="state"
                            keys={charts.state_series || []}
                            layout="vertical" height={360} />
          </Paper>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Party representation by state
            </Typography>
            <StatStackedBar data={charts.by_party || []} xKey="party"
                            keys={charts.party_series || []}
                            layout="vertical" height={400} />
          </Paper>
        </>
      )}
    </ExploreStatsPage>
  );
}
